<script setup>
definePage({
  meta: {
    layout: 'default',
    action: 'update',
    subject: 'Role',
  },
})

const { useApi } = await import('@/utils/api')
const router = useRouter()
const route = useRoute()

const roleId = route.params.id

// ── State ────────────────────────────────────────────────────────────────────
const role = ref(null)
const permissionGroups = ref([])
const selectedPerms = ref(new Set())
const loading = ref(true)
const saving = ref(false)
const errors = ref({})

// ── Fetch ─────────────────────────────────────────────────────────────────────
const fetchAll = async () => {
  loading.value = true
  try {
    const [roleRes, permsRes] = await Promise.all([
      useApi(`/api/v1/roles/${roleId}`).get(),
      useApi('/api/v1/permissions').get(),
    ])

    const roleData = roleRes?.data ?? roleRes
    const permsData = permsRes?.data ?? permsRes

    role.value = roleData
    permissionGroups.value = Array.isArray(permsData)
      ? permsData
      : (permsData?.data ?? [])

    selectedPerms.value = new Set(roleData.permissions ?? [])
  } catch (e) {
    console.error(e)
    router.push({ name: 'apps-roles' })
  } finally {
    loading.value = false
  }
}

onMounted(fetchAll)

// ── Permission helpers ────────────────────────────────────────────────────────
const isSelected = (perm) => selectedPerms.value.has(perm)

const togglePerm = (perm) => {
  if (selectedPerms.value.has(perm)) {
    selectedPerms.value.delete(perm)
  } else {
    selectedPerms.value.add(perm)
  }
  selectedPerms.value = new Set(selectedPerms.value)
}

const isGroupAllSelected = (group) =>
  group.permissions.every(p => selectedPerms.value.has(p))

const isGroupPartial = (group) =>
  group.permissions.some(p => selectedPerms.value.has(p)) && !isGroupAllSelected(group)

const toggleGroup = (group) => {
  if (isGroupAllSelected(group)) {
    group.permissions.forEach(p => selectedPerms.value.delete(p))
  } else {
    group.permissions.forEach(p => selectedPerms.value.add(p))
  }
  selectedPerms.value = new Set(selectedPerms.value)
}

const selectAll = () => {
  permissionGroups.value.forEach(g =>
    g.permissions.forEach(p => selectedPerms.value.add(p)),
  )
  selectedPerms.value = new Set(selectedPerms.value)
}

const clearAll = () => {
  selectedPerms.value = new Set()
}

const totalSelected = computed(() => selectedPerms.value.size)
const totalPermissions = computed(() =>
  permissionGroups.value.reduce((acc, g) => acc + g.permissions.length, 0),
)

const progressPercentage = computed(() => {
  if (totalPermissions.value === 0) return 0
  return Math.round((totalSelected.value / totalPermissions.value) * 100)
})

// ── Group label mapping ───────────────────────────────────────────────────────
const groupLabels = {
  dashboard: 'Dashboard',
  product: 'Produk & Katalog',
  inventory: 'Inventory & Bahan Baku',
  purchase: 'Pembelian',
  sales: 'Penjualan & POS',
  expense: 'Pengeluaran',
  report: 'Laporan',
  settings: 'Pengaturan',
  user: 'User & Role',
  audit: 'Audit Log',
}

const groupIcons = {
  dashboard: 'ri-dashboard-line',
  product: 'ri-cup-line',
  inventory: 'ri-archive-line',
  purchase: 'ri-truck-line',
  sales: 'ri-shopping-cart-line',
  expense: 'ri-wallet-3-line',
  report: 'ri-bar-chart-line',
  settings: 'ri-settings-3-line',
  user: 'ri-team-line',
  audit: 'ri-file-search-line',
}

const groupColors = {
  dashboard: 'primary',
  product: 'success',
  inventory: 'info',
  purchase: 'warning',
  sales: 'secondary',
  expense: 'error',
  report: 'purple',
  settings: 'teal',
  user: 'pink',
  audit: 'orange',
}

const permLabel = (perm) => {
  const action = perm.split('.')[1] ?? perm
  const labels = {
    read: 'Lihat',
    create: 'Tambah',
    update: 'Edit',
    delete: 'Hapus',
    void: 'Void',
  }
  return labels[action] ?? action
}

const permIcon = (perm) => {
  const action = perm.split('.')[1] ?? perm
  const icons = {
    read: 'ri-eye-line',
    create: 'ri-add-line',
    update: 'ri-edit-line',
    delete: 'ri-delete-bin-line',
    void: 'ri-close-circle-line',
  }
  return icons[action] ?? 'ri-checkbox-blank-circle-line'
}

// ── Save ──────────────────────────────────────────────────────────────────────
const save = async () => {
  saving.value = true
  errors.value = {}
  try {
    await useApi(`/api/v1/roles/${roleId}`).put({
      body: {
        name: role.value.name,
        permissions: Array.from(selectedPerms.value),
      },
    })
    router.push({ name: 'apps-roles' })
  } catch (e) {
    if (e?.response?.status === 422) {
      errors.value = e?.data?.errors ?? {}
    } else {
      console.error(e)
    }
  } finally {
    saving.value = false
  }
}
</script>

<template>
  <div>
    <!-- Page Header -->
    <VRow class="mb-6">
      <VCol cols="12">
        <div class="d-flex align-center gap-3 flex-wrap">
          <VBtn
            variant="tonal"
            color="secondary"
            size="small"
            icon="ri-arrow-left-line"
            @click="router.push({ name: 'apps-roles' })"
          />
          <div>
            <VBreadcrumbs
              :items="[
                { title: 'Role', to: { name: 'apps-roles' } },
                { title: 'Edit Permission', disabled: true },
              ]"
              class="px-0 mb-1"
            />
            <h4 class="text-h4 font-weight-bold mb-1">
              Edit Permission: <span class="text-primary">{{ role?.name }}</span>
            </h4>
            <p class="text-body-2 text-medium-emphasis mb-0">
              Kelola akses dan permission untuk role ini
            </p>
          </div>
        </div>
      </VCol>
    </VRow>

    <!-- Loading -->
    <VCard v-if="loading">
      <VCardText class="pa-6">
        <VSkeletonLoader type="heading, divider, list-item-two-line@6" />
      </VCardText>
    </VCard>

    <template v-else-if="role">
      <VRow>
        <!-- Permission Matrix -->
        <VCol cols="12" lg="9">
          <VCard>
            <VCardTitle class="pa-5 pb-3">
              <div class="d-flex align-center justify-space-between flex-wrap gap-3">
                <div class="d-flex align-center gap-3">
                  <VAvatar color="primary" variant="tonal" size="40">
                    <VIcon icon="ri-shield-keyhole-line" size="24" />
                  </VAvatar>
                  <div>
                    <div class="text-h6 font-weight-bold">Permission Matrix</div>
                    <div class="text-body-2 text-medium-emphasis">
                      Pilih permission yang ingin diberikan
                    </div>
                  </div>
                </div>
                <div class="d-flex gap-2">
                  <VBtn
                    variant="tonal"
                    color="secondary"
                    size="small"
                    prepend-icon="ri-checkbox-blank-line"
                    @click="clearAll"
                  >
                    Hapus Semua
                  </VBtn>
                  <VBtn
                    variant="tonal"
                    color="primary"
                    size="small"
                    prepend-icon="ri-checkbox-line"
                    @click="selectAll"
                  >
                    Pilih Semua
                  </VBtn>
                </div>
              </div>
            </VCardTitle>

            <VDivider />

            <VCardText class="pa-0">
              <!-- Permission Groups -->
              <div v-for="(group, index) in permissionGroups" :key="group.group">
                <!-- Group Header -->
                <div
                  class="d-flex align-center gap-3 px-6 py-4 cursor-pointer group-header"
                  :class="index % 2 === 0 ? 'bg-surface-variant-light' : ''"
                  @click="toggleGroup(group)"
                >
                  <VCheckbox
                    :model-value="isGroupAllSelected(group)"
                    :indeterminate="isGroupPartial(group)"
                    color="primary"
                    hide-details
                    density="compact"
                    @click.stop="toggleGroup(group)"
                  />
                  <VAvatar
                    :color="groupColors[group.group] || 'primary'"
                    variant="tonal"
                    size="32"
                  >
                    <VIcon
                      :icon="groupIcons[group.group] || 'ri-folder-line'"
                      size="18"
                    />
                  </VAvatar>
                  <span class="font-weight-semibold text-body-1">
                    {{ groupLabels[group.group] || group.group }}
                  </span>
                  <VChip
                    size="small"
                    :color="isGroupAllSelected(group) ? 'success' : 'secondary'"
                    variant="tonal"
                    label
                    class="ml-auto"
                  >
                    {{ group.permissions.filter(p => selectedPerms.has(p)).length }}
                    / {{ group.permissions.length }}
                  </VChip>
                </div>

                <!-- Permission Chips Grid -->
                <div class="px-6 py-4">
                  <VRow>
                    <VCol
                      v-for="perm in group.permissions"
                      :key="perm"
                      cols="6"
                      sm="4"
                      md="3"
                    >
                      <VCard
                        class="permission-card cursor-pointer"
                        :class="{ 'permission-card-selected': isSelected(perm) }"
                        variant="outlined"
                        @click="togglePerm(perm)"
                      >
                        <VCardText class="pa-3">
                          <div class="d-flex align-center gap-2">
                            <VCheckbox
                              :model-value="isSelected(perm)"
                              hide-details
                              density="compact"
                              color="primary"
                              @click.stop="togglePerm(perm)"
                            />
                            <VIcon
                              :icon="permIcon(perm)"
                              size="16"
                              :color="isSelected(perm) ? 'primary' : 'medium-emphasis'"
                            />
                            <span class="text-body-2 font-weight-medium">
                              {{ permLabel(perm) }}
                            </span>
                          </div>
                        </VCardText>
                      </VCard>
                    </VCol>
                  </VRow>
                </div>

                <VDivider v-if="index < permissionGroups.length - 1" />
              </div>
            </VCardText>
          </VCard>
        </VCol>

        <!-- Sidebar: Summary & Actions -->
        <VCol cols="12" lg="3">
          <!-- Summary Card -->
          <VCard class="mb-4">
            <VCardText class="pa-6 text-center">
              <VAvatar
                :color="progressPercentage > 50 ? 'success' : 'primary'"
                variant="tonal"
                size="64"
                class="mb-3"
              >
                <VIcon icon="ri-shield-check-line" size="32" />
              </VAvatar>
              <div class="text-h4 font-weight-bold mb-1">
                {{ totalSelected }}
              </div>
              <div class="text-body-2 text-medium-emphasis mb-4">
                dari {{ totalPermissions }} permission dipilih
              </div>

              <VProgressLinear
                :model-value="progressPercentage"
                :color="progressPercentage > 50 ? 'success' : 'primary'"
                rounded
                height="8"
                class="mb-2"
              />
              <div class="text-caption text-medium-emphasis">
                {{ progressPercentage }}% terpilih
              </div>
            </VCardText>
          </VCard>

          <!-- Quick Stats -->
          <VCard class="mb-4">
            <VCardTitle class="pa-5 pb-3">
              <div class="text-body-1 font-weight-semibold">Statistik</div>
            </VCardTitle>
            <VCardText class="pa-5 pt-0">
              <div class="d-flex flex-column gap-3">
                <div
                  v-for="group in permissionGroups"
                  :key="group.group"
                  class="d-flex align-center justify-space-between"
                >
                  <div class="d-flex align-center gap-2">
                    <VIcon
                      :icon="groupIcons[group.group] || 'ri-folder-line'"
                      size="16"
                      :color="groupColors[group.group] || 'primary'"
                    />
                    <span class="text-body-2">
                      {{ groupLabels[group.group] || group.group }}
                    </span>
                  </div>
                  <VChip
                    size="x-small"
                    :color="isGroupAllSelected(group) ? 'success' : 'secondary'"
                    variant="tonal"
                    label
                  >
                    {{ group.permissions.filter(p => selectedPerms.has(p)).length }}/{{ group.permissions.length }}
                  </VChip>
                </div>
              </div>
            </VCardText>
          </VCard>

          <!-- Actions Card -->
          <VCard>
            <VCardText class="pa-5">
              <VAlert
                v-if="errors.permissions"
                type="error"
                variant="tonal"
                density="compact"
                class="mb-4"
              >
                {{ errors.permissions?.[0] }}
              </VAlert>

              <VBtn
                color="primary"
                block
                size="large"
                :loading="saving"
                prepend-icon="ri-save-line"
                @click="save"
              >
                Simpan Permission
              </VBtn>

              <VBtn
                variant="tonal"
                color="secondary"
                block
                class="mt-3"
                :disabled="saving"
                @click="router.push({ name: 'apps-roles' })"
              >
                Batal
              </VBtn>
            </VCardText>
          </VCard>
        </VCol>
      </VRow>
    </template>
  </div>
</template>

<style scoped>
.cursor-pointer {
  cursor: pointer;
  transition: all 0.2s ease;
}

.cursor-pointer:hover {
  background-color: rgba(var(--v-theme-on-surface), 0.04);
}

.bg-surface-variant-light {
  background-color: rgba(var(--v-theme-on-surface), 0.02);
}

.permission-card {
  transition: all 0.2s ease;
  border-radius: 8px;
}

.permission-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.permission-card-selected {
  border-color: rgba(var(--v-theme-primary), 0.5);
  background-color: rgba(var(--v-theme-primary), 0.04);
}

.permission-card-selected:hover {
  background-color: rgba(var(--v-theme-primary), 0.08);
}

.group-header {
  border-radius: 8px 8px 0 0;
}

.group-header:hover {
  background-color: rgba(var(--v-theme-on-surface), 0.06);
}
</style>
